import { useCallback, useEffect, useMemo } from "react";
import { isBubbleMode } from "@/utils/bubble-mode";

interface UseBubbleEmbedOptions {
  chatId: string;
  agentId?: string | null;
}

interface UseBubbleEmbedReturn {
  /** Whether the current page is in bubble embed mode (computed once). */
  isBubbleMode: boolean;
  /** Sends a taqat-chat-close postMessage to the parent window. */
  notifyParentClose: () => void;
}

/** The ready handshake is sent at most once per document. */
let hasSignalledReady = false;

/**
 * Tells the bubble loader its branded overlay can go.
 *
 * The loader (`public/bubble-chat-embed.js`) hides the booting iframe behind an
 * overlay and drops it on `taqat-chat-iframe-ready`. That message used to be
 * sent only from `<Chat>` (below), so any screen rendering INSTEAD of `<Chat>`
 * never signalled — and the pre-chat language gate is exactly such a screen.
 * Every first-time visitor to a gated agent therefore sat behind the overlay,
 * with a finished gate underneath it, until the loader gave up. It stayed
 * invisible day to day because it only reproduces on a cold session: once a
 * language is picked it is remembered and the gate never appears again.
 *
 * The host cannot work this out for itself — the widget is cross-origin in any
 * real deployment, so the loader cannot see whether the iframe has painted.
 * The signal has to come from inside, which is why this lives in the app.
 *
 * No-ops outside an iframe, so the standalone app is unaffected.
 *
 * @param payload - Optional chat/agent context echoed back to the host.
 */
export function notifyEmbedSurfaceReady(
  payload: { chatId?: string | null; agentId?: string | null } = {}
): void {
  if (hasSignalledReady) return;
  if (typeof window === "undefined") return;
  if (!window.parent || window.parent === window) return;

  hasSignalledReady = true;
  window.parent.postMessage({
    type: 'taqat-chat-iframe-ready',
    payload: {
      chatId: payload.chatId ?? null,
      agentId: payload.agentId ?? null,
      isReady: true,
    },
  }, '*');
}

/**
 * Hook that encapsulates all bubble/embed side effects.
 * Sets up PostMessage listener for parent-to-iframe communication and
 * provides a callback to notify the parent when the chat should close.
 */
export function useBubbleEmbed({ chatId, agentId }: UseBubbleEmbedOptions): UseBubbleEmbedReturn {
  const bubbleMode = useMemo(() => isBubbleMode(), []);

  /**
   * Handle PostMessage communication from bubble embed script.
   * Responds to iframe-ready messages and configuration requests.
   */
  useEffect(() => {
    // Only set up PostMessage handling if we're in an iframe (embedded mode)
    if (window.self === window.top) return;

    /**
     * Handle incoming PostMessage events from parent window.
     * Processes configuration and control messages from bubble embed script.
     */
    const handlePostMessage = (event: MessageEvent) => {
      switch (event.data.type) {
        case 'taqat-chat-config':
          if (event.data.payload) {
            const { isToggledByButton, isDraggable, agentId: configAgentId, apikey } = event.data.payload;
            console.log('Received chat config:', {
              isToggledByButton,
              isDraggable,
              agentId: configAgentId,
              hasApiKey: !!apikey
            });
          }
          break;

        case 'taqat-chat-theme-change':
          if (event.data.payload?.theme) {
            console.log('Received theme change:', event.data.payload.theme);
          }
          break;

        default:
          break;
      }
    };

    window.addEventListener('message', handlePostMessage);

    // Report ready to the host loader. Normally a no-op: the page has already
    // signalled the moment it settled on a screen (lib/embed/ready-signal.ts),
    // and the send is idempotent. Kept so any other iframe host of <Chat> —
    // e.g. the standalone team chat surface — keeps working.
    const readyTimeout = setTimeout(() => {
      notifyEmbedSurfaceReady({ chatId, agentId });
    }, 100);

    return () => {
      window.removeEventListener('message', handlePostMessage);
      clearTimeout(readyTimeout);
    };
  }, [chatId, agentId]);

  /**
   * Send close message to parent window (for bubble embed).
   * Notifies the parent window when the chat should be closed.
   */
  const notifyParentClose = useCallback(() => {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage({
        type: 'taqat-chat-close',
        payload: {
          chatId,
          timestamp: Date.now()
        }
      }, '*');
    }
  }, [chatId]);

  return { isBubbleMode: bubbleMode, notifyParentClose };
}
